import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{title}</Typography>
    {children}
  </Box>
);

const Privacy = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Typography variant="h4" component="h1" gutterBottom>Privacy Policy</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
      Last updated: June 2026
    </Typography>
    <Divider sx={{ mb: 4 }} />

    <Section title="Who we are">
      <Typography variant="body1">
        KBros Leather is a handcrafted leather goods shop based in the United States. We sell
        custom leather products through this website and on Etsy. You can reach us at{' '}
        <a href="mailto:info@kbrosleather.com">info@kbrosleather.com</a>.
      </Typography>
    </Section>

    <Section title="What information we collect">
      <Typography variant="body1" paragraph>
        When you create an account or place an order, we collect:
      </Typography>
      <ul>
        <Typography component="li" variant="body1">Name and email address</Typography>
        <Typography component="li" variant="body1">Shipping address</Typography>
        <Typography component="li" variant="body1">Phone number (optional)</Typography>
        <Typography component="li" variant="body1">
          Payment information — processed by Stripe; we never store your card number
        </Typography>
        <Typography component="li" variant="body1">
          If you sign in with Google, your name and email are provided by Google
        </Typography>
      </ul>
    </Section>

    <Section title="How we use your information">
      <ul>
        <Typography component="li" variant="body1">To fulfill and ship your orders</Typography>
        <Typography component="li" variant="body1">To send order confirmation and shipping emails</Typography>
        <Typography component="li" variant="body1">To maintain your account and order history</Typography>
        <Typography component="li" variant="body1">To respond to questions or support requests</Typography>
      </ul>
      <Typography variant="body1" sx={{ mt: 2 }}>
        We do not sell your personal information to third parties. We do not use your data for
        advertising or send unsolicited marketing emails.
      </Typography>
    </Section>

    <Section title="Third-party services">
      <Typography variant="body1" paragraph>
        We use the following services to operate the store:
      </Typography>
      <ul>
        <Typography component="li" variant="body1">
          <strong>Stripe</strong> — payment processing. Your card details go directly to Stripe
          and are subject to{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            Stripe's Privacy Policy
          </a>.
        </Typography>
        <Typography component="li" variant="body1">
          <strong>Supabase</strong> — secure database and authentication hosting.
        </Typography>
        <Typography component="li" variant="body1">
          <strong>Google</strong> — optional sign-in via Google OAuth. If you choose to sign in
          with Google, we receive your name and email from Google and store them in your account.
          See{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google's Privacy Policy
          </a>.
        </Typography>
        <Typography component="li" variant="body1">
          <strong>Amazon Web Services (AWS)</strong> — website hosting and content delivery.
        </Typography>
      </ul>
    </Section>

    <Section title="Cookies and local storage">
      <Typography variant="body1">
        We use browser local storage to keep you signed in between visits and to remember your
        shopping cart. We do not use third-party tracking cookies or advertising cookies.
      </Typography>
    </Section>

    <Section title="Data retention">
      <Typography variant="body1">
        Order records are retained for at least seven years for tax and accounting purposes.
        You may request deletion of your account at any time by emailing us; we will remove
        your personal information from active systems within 30 days, subject to any legal
        retention obligations.
      </Typography>
    </Section>

    <Section title="Your rights">
      <Typography variant="body1" paragraph>
        You may request a copy of the personal data we hold about you, ask us to correct
        inaccurate data, or ask us to delete your account. Email us at{' '}
        <a href="mailto:info@kbrosleather.com">info@kbrosleather.com</a> to make a request.
      </Typography>
      <Typography variant="body1">
        If you are a California resident, you have additional rights under the CCPA, including
        the right to know what personal information we collect and the right to request deletion.
      </Typography>
    </Section>

    <Section title="Changes to this policy">
      <Typography variant="body1">
        We may update this policy occasionally. Material changes will be noted by updating the
        date at the top of this page. Continued use of the site after a change constitutes
        acceptance of the updated policy.
      </Typography>
    </Section>

    <Section title="Contact">
      <Typography variant="body1">
        Questions about this policy? Email{' '}
        <a href="mailto:info@kbrosleather.com">info@kbrosleather.com</a>.
      </Typography>
    </Section>
  </Container>
);

export default Privacy;
